import React, { useMemo, useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { IngredientPurchaseRecord } from '../../../../types';
import './ExpensesChart.scss';

interface ExpensesChartProps {
  purchases: IngredientPurchaseRecord[];
}

const formatCurrency = (value: number) => 
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value);

const ExpensesChart: React.FC<ExpensesChartProps> = ({ purchases }) => {
  const [colorMode, setColorMode] = useState<string>('dark');

  // Load colorMode from localStorage
  useEffect(() => {
    const mode = localStorage.getItem('colorMode') || 'dark';
    setColorMode(mode);

    const handleStorageChange = () => {
      const newMode = localStorage.getItem('colorMode') || 'dark';
      setColorMode(newMode);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const chartData = useMemo(() => {
    if (!purchases || purchases.length === 0) return { dates: [], expenses: [], counts: [] };

    // Group purchases by date
    const dateMap = new Map<string, { total: number; count: number }>();

    purchases.forEach(p => {
      const date = p.timestamp.split('T')[0]; // Get YYYY-MM-DD
      const existing = dateMap.get(date) || { total: 0, count: 0 };
      dateMap.set(date, {
        total: existing.total + p.total_neto,
        count: existing.count + 1,
      });
    });

    // Sort by date
    const sortedDates = Array.from(dateMap.keys()).sort();
    const expenses = sortedDates.map(date => Math.round(dateMap.get(date)!.total));
    const counts = sortedDates.map(date => dateMap.get(date)!.count);

    // Format dates for display
    const formattedDates = sortedDates.map(date => {
      const d = new Date(date + 'T12:00:00');
      return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
    });

    return { dates: formattedDates, expenses, counts };
  }, [purchases]);

  const textColor = colorMode === 'light' ? '#6b6b6b' : '#FFFFFF';
  const CHART_COLOR = '#FC7556';

  const option = {
    color: [CHART_COLOR],
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 1200,
    animationEasing: 'cubicInOut' as const,
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: {
        type: 'cross' as const,
        lineStyle: {
          color: CHART_COLOR,
          width: 2,
          type: 'dashed' as const
        },
        label: {
          backgroundColor: CHART_COLOR,
          color: '#ffffff',
          fontWeight: 600,
          borderRadius: 6,
          padding: [8, 12]
        }
      },
      backgroundColor: colorMode === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(249, 250, 251, 0.95)',
      borderColor: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
      borderWidth: 1,
      borderRadius: 12,
      textStyle: {
        color: textColor,
        fontWeight: 500,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      padding: [12, 16],
      boxShadow: colorMode === 'dark' 
        ? '0 10px 25px rgba(0, 0, 0, 0.3)' 
        : '0 10px 25px rgba(0, 0, 0, 0.1)',
      formatter: function(params: any) {
        const param = params[0];
        return `
          <div style="font-weight: 700; margin-bottom: 8px; font-size: 13px; color: ${CHART_COLOR};">
            ${param.axisValue}
          </div>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <span style="display: inline-block; width: 10px; height: 10px; background: ${CHART_COLOR}; border-radius: 50%;"></span>
            <span style="font-weight: 600;">Gasto Total:</span>
            <span style="margin-left: auto; font-weight: 700;">${formatCurrency(param.value)}</span>
          </div>
          <div style="font-size: 11px; color: rgba(${colorMode === 'dark' ? '255,255,255' : '0,0,0'}, 0.6); margin-top: 4px;">
            ${chartData.counts[param.dataIndex]} compras realizadas
          </div>
        `;
      }
    },
    xAxis: {
      type: 'category' as const,
      data: chartData.dates,
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)',
          width: 1
        }
      },
      axisTick: { show: false },
      axisLabel: {
        color: textColor,
        fontSize: 11,
        fontWeight: 500,
        margin: 12,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }
    },
    yAxis: {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: textColor,
        fontSize: 11,
        fontWeight: 500,
        margin: 12,
        formatter: (val: number) => formatCurrency(val),
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      splitLine: {
        lineStyle: {
          color: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)',
          type: 'dashed' as const,
          width: 1
        }
      }
    },
    grid: {
      left: '4%',
      right: '4%',
      bottom: '12%',
      top: '10%',
      containLabel: true
    },
    series: [{
      name: 'Gastos',
      type: 'line' as const,
      data: chartData.expenses,
      smooth: 0.3,
      lineStyle: {
        width: 3.5,
        color: CHART_COLOR,
        shadowColor: `${CHART_COLOR}40`,
        shadowBlur: 8,
        shadowOffsetY: 2
      },
      areaStyle: {
        color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${CHART_COLOR}25` },
          { offset: 0.7, color: `${CHART_COLOR}10` },
          { offset: 1, color: `${CHART_COLOR}05` }
        ])
      },
      itemStyle: {
        color: CHART_COLOR,
        borderWidth: 3,
        borderColor: 'transparent',
        shadowColor: `${CHART_COLOR}60`,
        shadowBlur: 6
      },
      emphasis: {
        scale: true,
        scaleSize: 12,
        itemStyle: { shadowBlur: 10, shadowColor: `${CHART_COLOR}80` }
      },
      symbolSize: 8,
      symbol: 'circle'
    }]
  };

  return (
    <div className="expenses-chart-wrapper">
      <div className="chart-header">
        <h3>Evolución de Gastos</h3>
        <p>Gastos totales durante el período seleccionado</p>
      </div>
      <div className="chart-container">
        <ReactECharts 
          option={option} 
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    </div>
  );
};

export default ExpensesChart;
